import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import { useSocket } from "../../../../SocketContext";
import { getUserSessions } from "../../../../store/features/getUserSessions";
import { ROOT_URL } from "../../../../api/ApiConfig";
import { emitAgentSetup } from '../../../../api/createAppSocket';
import { forceAuthExpiry } from '../../../../api/authHttp';
import SettingsContent from "../shared/SettingsContent";
import SettingsPageHeader from "../shared/SettingsPageHeader";
import { COLORS, CONTENT_MAX_WIDTH, FONT, errorTextSx } from "../shared/styles";
import SessionCard from "./SessionCard";
import SessionCardSkeleton from "./SessionCardSkeleton";
import SessionLimitBanner from "./SessionLimitBanner";

function ActiveSessionsPage() {
  const dispatch = useDispatch();
  const socket = useSocket();
  const [loggingOutId, setLoggingOutId] = useState(null);
  const [forceLogoutLoading, setForceLogoutLoading] = useState(false);

  const userData = useSelector((state) => state.loginToken);
  const currentSessionId = userData?.response?.sessionId;
  const userId = userData?.response?.user?._id;

  const { sessions = [], loading, error } = useSelector(
    (state) => state.getUserSessions,
  );

  const handleForceLogout = useCallback(
    ({ sessionId }) => {
      if (sessionId === currentSessionId) {
        forceAuthExpiry();
      }
    },
    [currentSessionId],
  );

  useEffect(() => {
    if (!socket || !userId || !currentSessionId) return undefined;

    if (!socket.connected) socket.connect();
    emitAgentSetup(socket);

    const joinSessionRoom = () => {
      socket.emit("join-session", { sessionId: currentSessionId });
    };

    joinSessionRoom();
    socket.on("connect", joinSessionRoom);
    socket.on("force-logout", handleForceLogout);
    return () => {
      socket.off("connect", joinSessionRoom);
      socket.off("force-logout", handleForceLogout);
    };
  }, [socket, userId, currentSessionId, handleForceLogout]);

  useEffect(() => {
    if (userId) dispatch(getUserSessions());
  }, [dispatch, userId]);

  const handleLogoutDevice = async (sessionId) => {
    try {
      setLoggingOutId(sessionId);
      const res = await fetch(`${ROOT_URL}/master-user/logout/${sessionId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        throw new Error("Logout failed");
      }

      if (socket?.connected) {
        socket.emit("session-logout", { sessionId });
      }

      setTimeout(() => dispatch(getUserSessions()), 500);
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      setLoggingOutId(null);
    }
  };

  const handleForceLogoutOthers = async () => {
    const otherSessions = sessions.filter(
      (session) => session.sessionId !== currentSessionId,
    );
    if (!otherSessions.length) return;

    setForceLogoutLoading(true);
    try {
      // One atomic revoke on the server — parallel per-session deletes race and
      // can leave other devices logged in.
      const res = await fetch(`${ROOT_URL}/master-user/logout-others`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        throw new Error("Force logout others failed");
      }

      const payload = await res.json().catch(() => ({}));
      const revokedIds = Array.isArray(payload?.revokedSessionIds)
        ? payload.revokedSessionIds
        : otherSessions.map((session) => session.sessionId);

      // Backup notify if HTTP already emitted force-logout.
      revokedIds.forEach((sessionId) => {
        if (socket?.connected) {
          socket.emit("session-logout", { sessionId });
        }
      });

      await dispatch(getUserSessions());
    } catch (err) {
      console.error("Force logout error:", err);
    } finally {
      setForceLogoutLoading(false);
    }
  };

  const sortedSessions = [...sessions].sort((a, b) => {
    if (a.sessionId === currentSessionId) return -1;
    if (b.sessionId === currentSessionId) return 1;
    return new Date(b.lastUsed) - new Date(a.lastUsed);
  });

  const otherSessionCount = sessions.filter(
    (session) => session.sessionId !== currentSessionId,
  ).length;

  return (
    <SettingsContent>
      <SettingsPageHeader
        title="Active Sessions"
        description="Manage your logged-in devices and sessions"
      />

      <SessionLimitBanner sessionCount={sessions.length} />

      <Box
        sx={{
          width: "100%",
          maxWidth: CONTENT_MAX_WIDTH,
          height: "1px",
          mt: "32px",
          bgcolor: "#E5E9EC",
        }}
      />

      {error ? (
        <Typography component="p" sx={{ ...errorTextSx, mt: "16px" }}>
          Could not load sessions. Please try again.
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          mt: "32px",
          width: "100%",
        }}
      >
        {loading
          ? Array.from({ length: 3 }).map((_, index) => (
              <SessionCardSkeleton key={index} />
            ))
          : sortedSessions.map((session) => {
              const isCurrent = session.sessionId === currentSessionId;
              return (
                <SessionCard
                  key={session._id || session.sessionId}
                  session={session}
                  isCurrent={isCurrent}
                  loggingOutId={loggingOutId}
                  onLogout={handleLogoutDevice}
                />
              );
            })}
      </Box>

      {otherSessionCount > 0 ? (
        <Button
          type="button"
          onClick={handleForceLogoutOthers}
          disabled={forceLogoutLoading}
          sx={{
            width: 248,
            height: 40,
            mt: "32px",
            borderRadius: "8px",
            border: `1px solid ${COLORS.border}`,
            background: COLORS.white,
            boxShadow: COLORS.shadow,
            color: COLORS.error,
            fontFamily: FONT.family,
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "18px",
            textTransform: "none",
            "&:disabled": {
              opacity: 0.6,
            },
          }}
        >
          {forceLogoutLoading
            ? "Logging out..."
            : "Force logout all other sessions"}
        </Button>
      ) : null}
    </SettingsContent>
  );
}

export default ActiveSessionsPage;
